import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, RefreshCw, FileDown, Mail, Lock, Filter, Wrench } from "lucide-react";
import { toast } from "sonner";

/**
 * 🆕 Feb 2026 — Registro Incassi (Cash Register).
 * Vista consolidata dei pagamenti effettuati, raggruppati per Broker,
 * con filtri per data e collaboratore. Include:
 *   - Stampa Report con voci configurabili (Premio Lordo, Fee/Diritti, Commissioni)
 *   - Invio email al broker selezionato
 *   - Chiusura Cassa: PDF snapshot del giorno
 */

const fmtEur = (n) => {
  const v = Number(n || 0);
  return "€ " + v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const [y, m, d] = String(iso).slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

export default function CashRegister() {
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  // 🆕 Feb 2026 — Filtri per data incasso provvigione broker (campo nuovo)
  const [commDateFrom, setCommDateFrom] = useState("");
  const [commDateTo, setCommDateTo] = useState("");
  const [brokerId, setBrokerId] = useState("__all__");
  const [userId, setUserId] = useState("__all__");
  const [brokers, setBrokers] = useState([]);
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [byBroker, setByBroker] = useState([]);
  const [totals, setTotals] = useState({ count: 0, amount: 0, gross_premium: 0, extra_fees: 0, commissions: 0 });
  const [loading, setLoading] = useState(false);
  // Dialog stampa
  const [printOpen, setPrintOpen] = useState(false);
  const [printOpts, setPrintOpts] = useState({
    broker_id: "__all__",
    include_gross_premium: true,
    include_extra_fees: false,
    include_commissions: false,
    send_email: false,
    to_email: "",
    cc: "",
    subject: "",
    message: "",
  });
  const [printBusy, setPrintBusy] = useState(false);
  // Dialog chiusura cassa
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeDate, setCloseDate] = useState(todayISO());
  const [closeBusy, setCloseBusy] = useState(false);
  // 🆕 Feb 2026 — Dialog Correzioni Incassi (admin/super_admin)
  const { user } = useAuth();
  const canFix = user?.role === "super_admin" || user?.role === "admin";
  const isSuperAdmin = user?.role === "super_admin";
  const [fixOpen, setFixOpen] = useState(false);
  const [fixBusy, setFixBusy] = useState(false);
  const [fixTolerance, setFixTolerance] = useState(1);
  const [fixCandidates, setFixCandidates] = useState([]);
  const [fixSelected, setFixSelected] = useState({}); // {policy_id+date: true}
  const [fixOverrides, setFixOverrides] = useState({}); // {policy_id+date: amount}

  const loadFilters = useCallback(async () => {
    try {
      const [rB, rU] = await Promise.all([
        api.get("/brokers"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setBrokers(Array.isArray(rB.data) ? rB.data : []);
      setUsers(Array.isArray(rU.data) ? rU.data : []);
    } catch (e) {
      console.warn("Errore caricamento filtri", e);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (brokerId && brokerId !== "__all__") params.broker_id = brokerId;
      if (userId && userId !== "__all__") params.user_id = userId;
      // 🆕 Feb 2026 — Filtri data incasso provvigione broker
      if (commDateFrom) params.comm_date_from = commDateFrom;
      if (commDateTo) params.comm_date_to = commDateTo;
      const { data } = await api.get("/cash-register", { params });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setByBroker(Array.isArray(data?.by_broker) ? data.by_broker : []);
      setTotals(data?.totals || { count: 0, amount: 0, gross_premium: 0, extra_fees: 0, commissions: 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, brokerId, userId, commDateFrom, commDateTo]);

  // 🆕 Feb 2026 — Salva data incasso provvigione broker inline (riga tabella)
  const saveCommBrokerPaidDate = useCallback(async (row, newDate) => {
    try {
      if (!row.policy_id || !row.due_date) {
        toast.error("Rata non modificabile (manca scadenza)");
        return;
      }
      await api.patch(`/cash-register/entries/${row.policy_id}/${row.due_date}/commission-broker-paid-date`, {
        date: newDate || null,
      });
      // Ottimistica: aggiorna lo stato locale
      setEntries((prev) => prev.map((e) => (e.id === row.id ? { ...e, commission_broker_paid_date: newDate || null } : e)));
      toast.success(newDate ? "Data incasso provvigione salvata" : "Data incasso provvigione rimossa");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { load(); }, [load]);

  // Raggruppa entries per broker per visualizzazione
  const groupedEntries = useMemo(() => {
    const groups = {};
    for (const e of entries) {
      const key = e.broker_id || "__direct__";
      if (!groups[key]) {
        groups[key] = {
          broker_id: e.broker_id,
          broker_name: e.broker_name,
          rows: [],
          subtotal: 0,
        };
      }
      groups[key].rows.push(e);
      groups[key].subtotal += e.amount || 0;
    }
    return Object.values(groups).sort((a, b) => b.subtotal - a.subtotal);
  }, [entries]);

  const openPrint = () => {
    setPrintOpts((p) => ({ ...p, broker_id: brokerId }));
    setPrintOpen(true);
  };

  const runPrint = async () => {
    setPrintBusy(true);
    try {
      const payload = {
        date_from: dateFrom,
        date_to: dateTo,
        broker_id: printOpts.broker_id !== "__all__" ? printOpts.broker_id : null,
        user_id: userId !== "__all__" ? userId : null,
        include_gross_premium: printOpts.include_gross_premium,
        include_extra_fees: printOpts.include_extra_fees,
        include_commissions: printOpts.include_commissions,
      };
      if (printOpts.send_email) {
        if (!payload.broker_id) {
          toast.error("Per inviare via email seleziona un broker specifico");
          return;
        }
        const emailPayload = {
          ...payload,
          to_email: printOpts.to_email || undefined,
          cc: printOpts.cc ? printOpts.cc.split(",").map((s) => s.trim()).filter(Boolean) : null,
          subject: printOpts.subject || undefined,
          message: printOpts.message || undefined,
        };
        const { data } = await api.post("/cash-register/report/email", emailPayload);
        toast.success(`Email inviata a ${data.sent_to}`);
        setPrintOpen(false);
      } else {
        const resp = await api.post("/cash-register/report/pdf", payload, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `registro_incassi_${dateFrom}_${dateTo}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("PDF scaricato");
        setPrintOpen(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPrintBusy(false);
    }
  };

  const runCloseDay = async () => {
    setCloseBusy(true);
    try {
      const resp = await api.post("/cash-register/close-day/pdf", { date: closeDate }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `chiusura_cassa_${closeDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Chiusura cassa del ${fmtDate(closeDate)} scaricata`);
      setCloseOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCloseBusy(false);
    }
  };

  const selectedPrintBroker = brokers.find((b) => b.id === printOpts.broker_id);

  // 🆕 Feb 2026 — Scan + Fix rate dimezzate
  const openFixDialog = async () => {
    setFixOpen(true);
    setFixBusy(true);
    setFixCandidates([]);
    setFixSelected({});
    setFixOverrides({});
    try {
      const { data } = await api.get("/admin/installments/scan-halved", { params: { tolerance_eur: fixTolerance } });
      const items = Array.isArray(data?.items) ? data.items : [];
      setFixCandidates(items);
      // Default: tutti selezionati
      const sel = {};
      const ovr = {};
      for (const r of items) {
        const k = `${r.policy_id}__${r.installment_date}`;
        sel[k] = true;
        ovr[k] = r.suggested_amount;
      }
      setFixSelected(sel);
      setFixOverrides(ovr);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFixBusy(false);
    }
  };

  const rescanFix = async () => {
    setFixBusy(true);
    try {
      const { data } = await api.get("/admin/installments/scan-halved", { params: { tolerance_eur: fixTolerance } });
      const items = Array.isArray(data?.items) ? data.items : [];
      setFixCandidates(items);
      const sel = {};
      const ovr = {};
      for (const r of items) {
        const k = `${r.policy_id}__${r.installment_date}`;
        sel[k] = true;
        ovr[k] = r.suggested_amount;
      }
      setFixSelected(sel);
      setFixOverrides(ovr);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFixBusy(false);
    }
  };

  const applyFixBatch = async () => {
    const items = fixCandidates
      .filter((r) => fixSelected[`${r.policy_id}__${r.installment_date}`])
      .map((r) => ({
        policy_id: r.policy_id,
        installment_date: r.installment_date,
        correct_amount: parseFloat(fixOverrides[`${r.policy_id}__${r.installment_date}`] ?? r.suggested_amount),
      }))
      .filter((it) => it.correct_amount > 0);
    if (items.length === 0) {
      toast.error("Seleziona almeno una rata da correggere");
      return;
    }
    if (!window.confirm(`Confermi la correzione di ${items.length} rate? L'operazione aggiorna gli importi e ricalcola le provvigioni già incassate.`)) return;
    setFixBusy(true);
    try {
      const { data } = await api.post("/admin/installments/fix-halved-batch", { items });
      toast.success(`${data.updated} rate corrette${data.errors?.length ? ` (${data.errors.length} errori)` : ""}`);
      setFixOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFixBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5" data-testid="cash-register-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-900">
            <Wallet className="w-6 h-6 text-emerald-600" />
            Registro Incassi
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Storico degli incassi (pagamenti conclusi + rate polizza), raggruppato per Broker. Filtra e stampa il report.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="cash-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={openPrint} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cash-print-open">
            <FileDown className="w-4 h-4 mr-1" />
            Stampa Report
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setCloseDate(todayISO()); setCloseOpen(true); }} className="border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" data-testid="cash-close-open">
            <Lock className="w-4 h-4 mr-1" />
            Chiusura Cassa
          </Button>
          {canFix && (
            <Button size="sm" variant="outline" onClick={openFixDialog} className="border-rose-400 bg-rose-50 text-rose-800 hover:bg-rose-100" data-testid="cash-fix-open">
              <Wrench className="w-4 h-4 mr-1" />
              Correzioni Incassi{isSuperAdmin ? " (tutte le agenzie)" : ""}
            </Button>
          )}
        </div>
      </div>

      {/* Filtri */}
      <div className="border rounded-xl bg-white shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-slate-600 font-semibold">
          <Filter className="w-4 h-4" /> Filtri
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label htmlFor="cash-date-from" className="text-xs text-slate-500 dark:text-slate-300">Incasso - dal</Label>
            <Input id="cash-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="cash-date-from" className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100" />
          </div>
          <div>
            <Label htmlFor="cash-date-to" className="text-xs text-slate-500 dark:text-slate-300">Incasso - al</Label>
            <Input id="cash-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="cash-date-to" className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100" />
          </div>
          <div>
            <Label htmlFor="cash-comm-from" className="text-xs text-indigo-600 dark:text-indigo-300 font-semibold">Provvigioni broker - dal</Label>
            <Input id="cash-comm-from" type="date" value={commDateFrom} onChange={(e) => setCommDateFrom(e.target.value)} data-testid="cash-comm-from" className="bg-white dark:bg-slate-800 border-indigo-300 dark:border-indigo-700 text-slate-900 dark:text-slate-100" />
          </div>
          <div>
            <Label htmlFor="cash-comm-to" className="text-xs text-indigo-600 dark:text-indigo-300 font-semibold">Provvigioni broker - al</Label>
            <Input id="cash-comm-to" type="date" value={commDateTo} onChange={(e) => setCommDateTo(e.target.value)} data-testid="cash-comm-to" className="bg-white dark:bg-slate-800 border-indigo-300 dark:border-indigo-700 text-slate-900 dark:text-slate-100" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 dark:text-slate-300">Broker</Label>
            <Select value={brokerId} onValueChange={setBrokerId}>
              <SelectTrigger data-testid="cash-broker-filter" className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti i broker</SelectItem>
                {brokers.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Collaboratore</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="cash-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* KPI Totali */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Incassi" value={totals.count} isNumber testid="kpi-count" />
        <KpiCard label="Totale incassato" value={fmtEur(totals.amount)} highlight="emerald" testid="kpi-amount" />
        <KpiCard label="Premio Lordo" value={fmtEur(totals.gross_premium)} testid="kpi-gross" />
        <KpiCard label="Fee/Diritti" value={fmtEur(totals.extra_fees)} testid="kpi-fees" />
        <KpiCard label="Provvigioni" value={fmtEur(totals.commissions)} testid="kpi-commissions" />
      </div>

      {/* Riepilogo per broker */}
      {byBroker.length > 0 && (
        <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Riepilogo per Broker
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Broker</th>
                <th className="text-center px-4 py-2">Incassi</th>
                <th className="text-right px-4 py-2">Totale incassato</th>
                <th className="text-right px-4 py-2">Premio Lordo</th>
                <th className="text-right px-4 py-2">Fee/Diritti</th>
                <th className="text-right px-4 py-2">Provvigioni</th>
              </tr>
            </thead>
            <tbody>
              {byBroker.map((g) => (
                <tr key={g.broker_id || "direct"} className="border-b last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-2 font-semibold">{g.broker_name}</td>
                  <td className="px-4 py-2 text-center">{g.count}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-700 font-semibold">{fmtEur(g.amount)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtEur(g.gross_premium)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtEur(g.extra_fees)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtEur(g.commissions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Elenco incassi raggruppato */}
      <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b text-xs font-semibold text-slate-600 uppercase tracking-wide">
          Elenco incassi
        </div>
        {loading && <div className="text-center py-10 text-slate-400 text-sm">Caricamento…</div>}
        {!loading && groupedEntries.length === 0 && (
          <div className="text-center py-10 text-slate-400 text-sm" data-testid="cash-empty">
            Nessun incasso nel periodo selezionato.
          </div>
        )}
        {!loading && groupedEntries.map((g) => (
          <div key={g.broker_id || "direct"} className="border-b last:border-b-0">
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between text-sm">
              <span className="font-semibold text-blue-900">▶ {g.broker_name}</span>
              <span className="text-xs text-blue-800">
                {g.rows.length} incassi · <span className="font-mono font-bold">{fmtEur(g.subtotal)}</span>
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-xs text-slate-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-2 w-28">Data incasso (cliente)</th>
                  <th className="text-left px-4 py-2">N° Polizza</th>
                  <th className="text-left px-4 py-2">Cliente</th>
                  <th className="text-left px-4 py-2">Collab.</th>
                  <th className="text-left px-4 py-2 w-24">Metodo</th>
                  <th className="text-right px-4 py-2 w-28">Incasso</th>
                  <th className="text-right px-4 py-2 w-28">Premio Lordo</th>
                  <th className="text-right px-4 py-2 w-24">Fee</th>
                  <th className="text-right px-4 py-2 w-24">Provv.</th>
                  <th className="text-left px-4 py-2 w-44 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200">Data Incasso Provvigioni (broker)</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`cash-entry-${r.id}`}>
                    <td className="px-4 py-2 text-slate-600 dark:text-slate-300">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2 font-mono text-slate-800 dark:text-slate-100">{r.policy_number}</td>
                    <td className="px-4 py-2 text-slate-800 dark:text-slate-100">{r.client_name}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">{r.user_name}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className="text-xs capitalize">{r.method}</Badge>
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-700 dark:text-emerald-300 font-semibold">{fmtEur(r.amount)}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-600 dark:text-slate-300">{fmtEur(r.gross_premium)}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-600 dark:text-slate-300">{fmtEur(r.extra_fees)}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-600 dark:text-slate-300">{fmtEur(r.commissions)}</td>
                    <td className="px-4 py-2">
                      {r.due_date ? (
                        <Input
                          type="date"
                          value={r.commission_broker_paid_date || ""}
                          onChange={(e) => saveCommBrokerPaidDate(r, e.target.value)}
                          data-testid={`cash-comm-paid-${r.id}`}
                          className={`h-7 text-xs w-full bg-white dark:bg-slate-800 border-indigo-300 dark:border-indigo-700 text-slate-900 dark:text-slate-100 ${r.commission_broker_paid_date ? "font-semibold text-indigo-800 dark:text-indigo-200 bg-indigo-50 dark:bg-indigo-900/40" : ""}`}
                          title="Data in cui le provvigioni di questa rata sono state incassate dal broker"
                        />
                      ) : (
                        <span className="text-[11px] text-slate-400 dark:text-slate-500 italic" title="Riga non associata a una rata (transazione): modifica non supportata">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Dialog Stampa */}
      <Dialog open={printOpen} onOpenChange={(o) => { if (!printBusy) setPrintOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="cash-print-dialog">
          <DialogHeader>
            <DialogTitle>Stampa Report Incassi</DialogTitle>
            <DialogDescription>
              Filtri correnti applicati (periodo, collaboratore). Scegli broker, voci da includere e destinazione.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Broker</Label>
              <Select value={printOpts.broker_id} onValueChange={(v) => setPrintOpts((p) => ({ ...p, broker_id: v }))}>
                <SelectTrigger data-testid="cash-print-broker"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti i broker</SelectItem>
                  {brokers.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Voci da includere nel PDF</div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={printOpts.include_gross_premium} onCheckedChange={(v) => setPrintOpts((p) => ({ ...p, include_gross_premium: !!v }))} data-testid="cash-print-gross" />
                  <span className="text-sm"><strong>Premio Lordo</strong> di polizza</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={printOpts.include_extra_fees} onCheckedChange={(v) => setPrintOpts((p) => ({ ...p, include_extra_fees: !!v }))} data-testid="cash-print-fees" />
                  <span className="text-sm">Includi <strong>Fee/Diritti</strong> (extra fees polizza)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={printOpts.include_commissions} onCheckedChange={(v) => setPrintOpts((p) => ({ ...p, include_commissions: !!v }))} data-testid="cash-print-comm" />
                  <span className="text-sm">Includi <strong>Commissioni extra</strong></span>
                </label>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                I totali nel PDF vengono ricalcolati solo sulle voci selezionate.
              </p>
            </div>

            <div className="border rounded-lg p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={printOpts.send_email} onCheckedChange={(v) => setPrintOpts((p) => ({ ...p, send_email: !!v }))} data-testid="cash-print-send-email" disabled={printOpts.broker_id === "__all__"} />
                <span className="text-sm font-semibold">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Invia via email al Broker selezionato
                </span>
              </label>
              {printOpts.broker_id === "__all__" && (
                <div className="text-[11px] text-amber-700">
                  ⚠️ Per inviare via email seleziona un broker specifico.
                </div>
              )}
              {printOpts.send_email && printOpts.broker_id !== "__all__" && (
                <div className="space-y-2 mt-2">
                  <div className="text-[11px] text-slate-500">
                    Destinatario predefinito: <strong>{selectedPrintBroker?.email || "— nessun'email configurata —"}</strong>
                  </div>
                  <Input
                    placeholder="Sovrascrivi email destinatario (opzionale)"
                    value={printOpts.to_email}
                    onChange={(e) => setPrintOpts((p) => ({ ...p, to_email: e.target.value }))}
                    data-testid="cash-print-to-email"
                  />
                  <Input
                    placeholder="CC (separati da virgola, opzionale)"
                    value={printOpts.cc}
                    onChange={(e) => setPrintOpts((p) => ({ ...p, cc: e.target.value }))}
                    data-testid="cash-print-cc"
                  />
                  <Input
                    placeholder="Oggetto (opzionale)"
                    value={printOpts.subject}
                    onChange={(e) => setPrintOpts((p) => ({ ...p, subject: e.target.value }))}
                    data-testid="cash-print-subject"
                  />
                  <Textarea
                    rows={3}
                    placeholder="Messaggio aggiuntivo (opzionale)"
                    value={printOpts.message}
                    onChange={(e) => setPrintOpts((p) => ({ ...p, message: e.target.value }))}
                    data-testid="cash-print-message"
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintOpen(false)} disabled={printBusy} data-testid="cash-print-cancel">Annulla</Button>
            <Button onClick={runPrint} disabled={printBusy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cash-print-confirm">
              {printBusy ? "Generazione…" : (printOpts.send_email ? "Invia email" : "Scarica PDF")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Chiusura Cassa */}
      <Dialog open={closeOpen} onOpenChange={(o) => { if (!closeBusy) setCloseOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="cash-close-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              Chiusura Cassa
            </DialogTitle>
            <DialogDescription>
              Genera un PDF riepilogativo di tutti gli incassi del giorno indicato con tutte le voci (Premio, Fee, Provvigioni). Non applica lock in DB — è uno snapshot stampabile.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="close-date" className="text-xs text-slate-500">Giorno da chiudere</Label>
            <Input id="close-date" type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} data-testid="cash-close-date" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)} disabled={closeBusy} data-testid="cash-close-cancel">Annulla</Button>
            <Button onClick={runCloseDay} disabled={closeBusy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="cash-close-confirm">
              {closeBusy ? "Generazione…" : "Scarica PDF Chiusura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog Correzioni Incassi (super_admin only) */}
      <Dialog open={fixOpen} onOpenChange={(o) => { if (!fixBusy) setFixOpen(o); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="cash-fix-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-rose-600" />
              Correzioni Incassi — Rate con importo dimezzato
            </DialogTitle>
            <DialogDescription>
              Lo scan trova rate dove <code>installment_amounts[d] ≈ (premium / n_rate) / 2</code> ± tolleranza. Puoi modificare l'importo corretto suggerito, poi "Applica" raddoppia le rate selezionate e ricalcola la provvigione <strong>solo</strong> per quelle già incassate.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-3 pb-3 border-b border-slate-200">
            <div>
              <Label className="text-xs text-slate-500">Tolleranza (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={fixTolerance}
                onChange={(e) => setFixTolerance(parseFloat(e.target.value) || 0)}
                className="w-28"
                data-testid="cash-fix-tolerance"
              />
            </div>
            <Button size="sm" variant="outline" onClick={rescanFix} disabled={fixBusy} data-testid="cash-fix-rescan">
              <RefreshCw className={`w-4 h-4 mr-1 ${fixBusy ? "animate-spin" : ""}`} />
              Riscansiona
            </Button>
            <div className="text-sm text-slate-600 ml-auto">
              <strong className="text-rose-700">{fixCandidates.length}</strong> rate candidate — <strong>{Object.values(fixSelected).filter(Boolean).length}</strong> selezionate
            </div>
          </div>
          <div className="flex-1 overflow-auto border rounded-md mt-3">
            {fixBusy && fixCandidates.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Scansione in corso…</div>
            ) : fixCandidates.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Nessuna rata dimezzata trovata con tolleranza €{fixTolerance.toFixed(2)}.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-100 border-b sticky top-0">
                  <tr>
                    <th className="px-2 py-2 w-10 text-left">
                      <Checkbox
                        checked={fixCandidates.length > 0 && fixCandidates.every((r) => fixSelected[`${r.policy_id}__${r.installment_date}`])}
                        onCheckedChange={(v) => {
                          const next = {};
                          for (const r of fixCandidates) next[`${r.policy_id}__${r.installment_date}`] = !!v;
                          setFixSelected(next);
                        }}
                        data-testid="cash-fix-select-all"
                      />
                    </th>
                    <th className="px-2 py-2 text-left">Agenzia</th>
                    <th className="px-2 py-2 text-left">Polizza</th>
                    <th className="px-2 py-2 text-left">Cliente</th>
                    <th className="px-2 py-2 text-left">Broker</th>
                    <th className="px-2 py-2 text-left">Fraz.</th>
                    <th className="px-2 py-2 text-left">Data rata</th>
                    <th className="px-2 py-2 text-right">Attuale</th>
                    <th className="px-2 py-2 text-right">Atteso</th>
                    <th className="px-2 py-2 text-right w-28">Correzione (€)</th>
                    <th className="px-2 py-2 text-center">Pagata</th>
                    <th className="px-2 py-2 text-center">Prov.incassata</th>
                  </tr>
                </thead>
                <tbody>
                  {fixCandidates.map((r) => {
                    const k = `${r.policy_id}__${r.installment_date}`;
                    return (
                      <tr key={k} className={`border-b hover:bg-slate-50 ${fixSelected[k] ? "bg-rose-50/40" : ""}`} data-testid={`cash-fix-row-${k}`}>
                        <td className="px-2 py-1">
                          <Checkbox
                            checked={!!fixSelected[k]}
                            onCheckedChange={(v) => setFixSelected((s) => ({ ...s, [k]: !!v }))}
                            data-testid={`cash-fix-sel-${k}`}
                          />
                        </td>
                        <td className="px-2 py-1 text-slate-600 max-w-[120px] truncate" title={r.agency_name}>{r.agency_name}</td>
                        <td className="px-2 py-1 font-mono text-slate-800">{r.policy_number}</td>
                        <td className="px-2 py-1">{r.client_name}</td>
                        <td className="px-2 py-1 text-slate-600">{r.broker_name}</td>
                        <td className="px-2 py-1 text-slate-500 capitalize">{r.frazionamento}</td>
                        <td className="px-2 py-1 font-mono text-slate-700">{fmtDate(r.installment_date)}</td>
                        <td className="px-2 py-1 text-right font-mono text-rose-700 font-semibold">{fmtEur(r.current_amount)}</td>
                        <td className="px-2 py-1 text-right font-mono text-emerald-700">{fmtEur(r.expected_amount)}</td>
                        <td className="px-2 py-1 text-right">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={fixOverrides[k] ?? r.suggested_amount}
                            onChange={(e) => setFixOverrides((o) => ({ ...o, [k]: e.target.value }))}
                            className="w-24 h-7 text-xs font-mono"
                            data-testid={`cash-fix-amt-${k}`}
                          />
                        </td>
                        <td className="px-2 py-1 text-center">
                          {r.is_paid ? <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-300 text-emerald-800">SÌ</Badge> : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {r.has_commission_collection ? <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-300 text-blue-800">SÌ (ricalcola)</Badge> : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixOpen(false)} disabled={fixBusy} data-testid="cash-fix-cancel">Annulla</Button>
            <Button
              onClick={applyFixBatch}
              disabled={fixBusy || Object.values(fixSelected).filter(Boolean).length === 0}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="cash-fix-apply"
            >
              {fixBusy ? "Applico…" : `Applica correzioni (${Object.values(fixSelected).filter(Boolean).length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, highlight, isNumber, testid }) {
  return (
    <div className={`border rounded-xl p-3 bg-white shadow-sm ${highlight === "emerald" ? "border-emerald-300 bg-emerald-50" : ""}`} data-testid={testid}>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-xl font-bold mt-1 ${highlight === "emerald" ? "text-emerald-700" : "text-slate-900"} ${isNumber ? "" : "font-mono"}`}>{value}</div>
    </div>
  );
}
